import { ENABLED_QUESTION_TYPES, QUESTION_TYPES, typeMeta } from "./constants";

/**
 * Client-side validation for the Question Bank module.
 * The backend must re-run every one of these rules.
 */

const QUESTION_TYPES_SET = new Set(ENABLED_QUESTION_TYPES);

const isBlank = (v) => !v || !String(v).trim();

export const validateBank = (data) => {
  const errors = {};
  if (isBlank(data.name)) errors.name = "Bank name is required";
  else if (String(data.name).trim().length > 190)
    errors.name = "Bank name must be 190 characters or fewer";
  if (data.description && String(data.description).length > 2000)
    errors.description = "Description must be 2000 characters or fewer";
  return errors;
};

export const validateUnit = (data) => {
  const errors = {};
  if (isBlank(data.name)) errors.name = "Unit name is required";
  else if (String(data.name).trim().length > 190)
    errors.name = "Unit name must be 190 characters or fewer";
  if (!data.question_bank_id)
    errors.question_bank_id = "A unit must belong to a question bank";
  if (data.display_order !== "" && data.display_order != null) {
    const n = Number(data.display_order);
    if (!Number.isFinite(n) || n < 0)
      errors.display_order = "Display order must be zero or greater";
  }
  return errors;
};

export const validateLecture = (data) => {
  const errors = {};
  if (isBlank(data.name)) errors.name = "Lecture name is required";
  else if (String(data.name).trim().length > 190)
    errors.name = "Lecture name must be 190 characters or fewer";
  if (!data.unit_id) errors.unit_id = "A lecture must belong to a unit";
  if (data.display_order !== "" && data.display_order != null) {
    const n = Number(data.display_order);
    if (!Number.isFinite(n) || n < 0)
      errors.display_order = "Display order must be zero or greater";
  }
  return errors;
};

/**
 * Validates a single question.
 * Returns an object of field errors; empty means valid.
 */
export const validateQuestion = (q, { requireLecture = true } = {}) => {
  const errors = {};

  if (isBlank(q.question_text)) {
    errors.question_text = "Question text is required";
  }
  if (requireLecture && !q.lecture_id) {
    errors.lecture_id = "A question must belong to a lecture";
  }
  if (!q.question_type || !QUESTION_TYPES_SET.has(q.question_type)) {
    errors.question_type = "Select a valid question type";
  }

  if (q.marks !== "" && q.marks != null) {
    const marks = Number(q.marks);
    if (!Number.isFinite(marks) || marks <= 0) {
      errors.marks = "Marks must be greater than zero";
    }
  }

  const meta = typeMeta(q.question_type);
  if (meta.hasOptions) {
    const options = Array.isArray(q.options) ? q.options : [];
    const filled = options.filter((o) => !isBlank(o.option_text));

    if (filled.length < meta.minOptions) {
      errors.options = `At least ${meta.minOptions} options are required`;
    } else if (filled.length !== options.length) {
      errors.options = "Every option must have text";
    } else {
      const seen = new Set();
      const duplicate = filled.some((o) => {
        const key = String(o.option_text).trim().toLowerCase();
        if (seen.has(key)) return true;
        seen.add(key);
        return false;
      });
      if (duplicate) errors.options = "Options must be unique";
    }

    const correct = filled.filter((o) => !!o.is_correct);
    if (correct.length === 0) {
      errors.correct = "At least one correct answer is required";
    } else if (!meta.multiCorrect && correct.length > 1) {
      errors.correct = "This question type allows only one correct answer";
    }
  }

  if (q.question_type === QUESTION_TYPES.SHORT_ANSWER && isBlank(q.correct_answer)) {
    errors.correct_answer = "A model answer is required for short answer questions";
  }

  return errors;
};

/**
 * Validates a whole bulk batch.
 * Returns { valid, rows: [{ index, errors }] } - rows only contains failures.
 */
export const validateQuestionBatch = (questions, opts) => {
  const rows = [];
  questions.forEach((q, index) => {
    const errors = validateQuestion(q, opts);
    if (Object.keys(errors).length) rows.push({ index, errors });
  });
  return { valid: rows.length === 0, rows };
};

export const firstError = (errors) => {
  const keys = Object.keys(errors || {});
  return keys.length ? errors[keys[0]] : null;
};
